function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function setError(errors, field, message) {
  if (!errors[field]) errors[field] = message;
}

function getMediaConnectTierLimit(tier) {
  const normalizedTier = String(tier || "").toUpperCase();
  if (normalizedTier.includes("20")) return 20000000;
  if (normalizedTier.includes("50")) return 50000000;
  if (normalizedTier.includes("100")) return 100000000;
  return null;
}

function validateCommonFields(model, errors) {
  if (isBlank(model?.name)) setError(errors, "name", "Name is required.");
  if (!Array.isArray(model?.claimsCanAccess) || model.claimsCanAccess.length === 0) {
    setError(errors, "claimsCanAccess", "At least one access claim is required.");
  }
  if (!Array.isArray(model?.claimsCanEdit) || model.claimsCanEdit.length === 0) {
    setError(errors, "claimsCanEdit", "At least one edit claim is required.");
  }
}

export function validateRouteEndpoints(endpoints = []) {
  const normalizedEndpoints = (Array.isArray(endpoints) ? endpoints : []).map((endpoint) => {
    const fieldErrors = {};

    if (isBlank(endpoint?.interfaceId)) {
      fieldErrors.interfaceId = "Interface ID is required.";
    } else {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(endpoint.interfaceId)) {
        fieldErrors.interfaceId = "Interface ID should use UUID format.";
      }
    }

    if (isBlank(endpoint?.interfaceName)) {
      fieldErrors.interfaceName = "Interface name is required.";
    }

    if (isBlank(endpoint?.streamManagerId)) {
      fieldErrors.streamManagerId = "Stream Router ID is required.";
    }

    if (String(endpoint?.type || "").toUpperCase() !== "SR") {
      fieldErrors.type = 'Type must be "SR".';
    }

    return {
      ...endpoint,
      _validationErrors: fieldErrors,
    };
  });

  return {
    isValid:
      normalizedEndpoints.length > 0 &&
      normalizedEndpoints.every((endpoint) => Object.keys(endpoint._validationErrors).length === 0),
    endpoints: normalizedEndpoints,
  };
}

export function validateInputForm(model, context = {}) {
  const errors = {};
  const input = model?.input || {};

  validateCommonFields(model, errors);

  if (isBlank(input.type)) setError(errors, "input.type", "Input type is required.");

  if (input.type === "ROUTE" && input.createInterface) {
    if (isBlank(input.streamRouterId)) {
      setError(errors, "input.streamRouterId", "Stream Router is required.");
    }
    if (isBlank(input.interfaceName)) {
      setError(errors, "input.interfaceName", "Interface name is required.");
    }
    if (isBlank(input?.source?.url)) {
      setError(errors, "input.source.url", "Source URL is required.");
    }
    if (isBlank(input?.destination?.url)) {
      setError(errors, "input.destination.url", "Destination URL is required.");
    }
  }

  if (input.type === "MEDIA_CONNECT") {
    if (isBlank(input.mcInputName)) setError(errors, "input.mcInputName", "Name is required.");
    if (isBlank(input.mcInputBitrate)) {
      setError(errors, "input.mcInputBitrate", "Maximum bitrate is required.");
    }
    if (isBlank(input.mcInputRoutingScope)) {
      setError(errors, "input.mcInputRoutingScope", "Routing scope is required.");
    }
    if (isBlank(input.mcInputTier)) setError(errors, "input.mcInputTier", "Tier is required.");
    if (isBlank(input.mcInputNetworkInterfaceArn)) {
      setError(errors, "input.mcInputNetworkInterfaceArn", "Network interface is required.");
    }
    if (isBlank(input.mcInputProtocol)) {
      setError(errors, "input.mcInputProtocol", "Protocol is required.");
    }

    if (input.mcInputProtocol === "SRT_LISTENER") {
      if (isBlank(input.mcInputPort)) setError(errors, "input.mcInputPort", "Port is required.");
      if (isBlank(input.mcInputLatency)) {
        setError(errors, "input.mcInputLatency", "Latency is required.");
      }
    }

    if (input.mcInputProtocol === "SRT_CALLER") {
      if (isBlank(input.mcInputSourceAddress)) {
        setError(errors, "input.mcInputSourceAddress", "Source address is required.");
      }
      if (isBlank(input.mcInputSourcePort)) {
        setError(errors, "input.mcInputSourcePort", "Source port is required.");
      }
      if (isBlank(input.mcInputLatency)) {
        setError(errors, "input.mcInputLatency", "Latency is required.");
      }
    }

    if (input.mcInputProtocol === "RTP" && isBlank(input.mcInputPort)) {
      setError(errors, "input.mcInputPort", "Port is required.");
    }

    if (input.mcInputProtocol === "RIST") {
      if (isBlank(input.mcInputPort)) setError(errors, "input.mcInputPort", "Port is required.");
      if (isBlank(input.mcInputRecoveryLatency)) {
        setError(errors, "input.mcInputRecoveryLatency", "Recovery latency is required.");
      }
    }
  }

  if (context.requireCreateAccounts && (!Array.isArray(model?.accountIds) || model.accountIds.length === 0)) {
    setError(errors, "accountIds", "Select at least one account.");
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

function validateWowzaTargetConfig(config, errors) {
  if (isBlank(config?.entryName)) {
    setError(errors, "output.wowzaTargetConfig.entryName", "Entry name is required.");
  }
  if (isBlank(config?.profile)) {
    setError(errors, "output.wowzaTargetConfig.profile", "Profile is required.");
  }
  if (isBlank(config?.host)) {
    setError(errors, "output.wowzaTargetConfig.host", "Host is required.");
  }

  if (config?.profile === "rtp") {
    if (isBlank(config?.videoPort)) {
      setError(errors, "output.wowzaTargetConfig.videoPort", "Video port is required.");
    }
    if (isBlank(config?.audioPort)) {
      setError(errors, "output.wowzaTargetConfig.audioPort", "Audio port is required.");
    }
    if (isBlank(config?.streamName)) {
      setError(errors, "output.wowzaTargetConfig.streamName", "Stream name is required.");
    }
    return;
  }

  if (isBlank(config?.port)) {
    setError(errors, "output.wowzaTargetConfig.port", "Port is required.");
  }

  if (["rtmp", "youtube", "mpegts", "rtp"].includes(config?.profile) && isBlank(config?.streamName)) {
    setError(errors, "output.wowzaTargetConfig.streamName", "Stream name is required.");
  }
}

export function validateOutputForm(model, context = {}) {
  const errors = {};
  const output = model?.output || {};

  validateCommonFields(model, errors);

  if (isBlank(output.outputType)) setError(errors, "output.outputType", "Output type is required.");
  if (isBlank(output.status)) setError(errors, "output.status", "Status is required.");
  if (output.outputType && output.outputType !== "MEDIA_CONNECT" && isBlank(output.location)) {
    setError(errors, "output.location", "Location is required.");
  }

  if (output.outputType === "OTT") {
    if (isBlank(output.wowzaServerId)) {
      setError(errors, "output.wowzaServerId", "Wowza server is required.");
    }
    if (isBlank(output.wowzaApplicationId)) {
      setError(errors, "output.wowzaApplicationId", "Wowza application is required.");
    }
    if (isBlank(output.ottPlatform)) {
      setError(errors, "output.ottPlatform", "OTT platform is required.");
    }

    if (output.allowExternalConfigUpdate && output.createWowzaTarget) {
      validateWowzaTargetConfig(output.wowzaTargetConfig || {}, errors);
    }
  }

  if (output.outputType === "ROUTE") {
    if (isBlank(output.streamRouterId)) {
      setError(errors, "output.streamRouterId", "Stream Router is required.");
    }
    if (isBlank(output.interfaceId)) {
      setError(errors, "output.interfaceId", "Interface is required.");
    }
  }

  if (output.outputType === "TECHEX") {
    if (isBlank(output.techexOutputId)) {
      setError(errors, "output.techexOutputId", "Techex Output ID is required.");
    }
    if (isBlank(output.techexEdgeId)) {
      setError(errors, "output.techexEdgeId", "Techex Edge ID is required.");
    }
  }

  if (output.outputType === "MEDIA_CONNECT") {
    if (isBlank(output.routingScope)) {
      setError(errors, "output.routingScope", "Routing scope is required.");
    }

    if (output.allowExternalConfigUpdate) {
      if (isBlank(output.mcOutputName)) {
        setError(errors, "output.mcOutputName", "MediaConnect name is required.");
      }
      if (isBlank(output.mcOutputBitrate)) {
        setError(errors, "output.mcOutputBitrate", "Maximum bitrate is required.");
      }
      if (isBlank(output.mcOutputTier)) {
        setError(errors, "output.mcOutputTier", "Tier is required.");
      }
      const bitrateLimit = getMediaConnectTierLimit(output.mcOutputTier);
      if (!isBlank(output.mcOutputBitrate) && bitrateLimit && Number(output.mcOutputBitrate) > bitrateLimit) {
        setError(
          errors,
          "output.mcOutputBitrate",
          `Maximum bitrate cannot exceed ${bitrateLimit.toLocaleString("en-US")} for the selected tier.`
        );
      }
      if (isBlank(output.mcOutputNetworkInterfaceArn)) {
        setError(errors, "output.mcOutputNetworkInterfaceArn", "Network interface is required.");
      }
      if (isBlank(output.mcOutputProtocol)) {
        setError(errors, "output.mcOutputProtocol", "Protocol is required.");
      }

      if (output.mcOutputProtocol === "SRT_CALLER") {
        if (isBlank(output.mcOutputDestinationAddress)) {
          setError(errors, "output.mcOutputDestinationAddress", "Destination address is required.");
        }
        if (isBlank(output.mcOutputDestinationPort)) {
          setError(errors, "output.mcOutputDestinationPort", "Destination port is required.");
        }
        if (isBlank(output.mcOutputLatency)) {
          setError(errors, "output.mcOutputLatency", "Latency is required.");
        }
      }

      if (output.mcOutputProtocol === "SRT_LISTENER") {
        if (isBlank(output.mcOutputPort)) {
          setError(errors, "output.mcOutputPort", "Port is required.");
        }
        if (isBlank(output.mcOutputLatency)) {
          setError(errors, "output.mcOutputLatency", "Latency is required.");
        }
      }

      if (["RTP", "RIST"].includes(output.mcOutputProtocol)) {
        if (isBlank(output.mcOutputDestinationAddress)) {
          setError(errors, "output.mcOutputDestinationAddress", "Destination address is required.");
        }
        if (isBlank(output.mcOutputDestinationPort)) {
          setError(errors, "output.mcOutputDestinationPort", "Destination port is required.");
        }
      }
    }
  }

  if (context.requireCreateAccounts && (!Array.isArray(model?.accountIds) || model.accountIds.length === 0)) {
    setError(errors, "accountIds", "Select at least one account.");
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateTranscodingForm(model) {
  const errors = {};
  validateCommonFields(model, errors);

  if (isBlank(model?.transcoding?.type)) {
    setError(errors, "transcoding.type", "Type is required.");
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateOutputProfileForm(model) {
  const errors = {};

  validateCommonFields(model, errors);

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateInputProfileForm(model) {
  const errors = {};

  validateCommonFields(model, errors);

  // config is AWSJSON! server side - reject unparsable input before the mutation
  const config = model?.inputProfile?.config;
  if (isBlank(config)) {
    setError(errors, "inputProfile.config", "Config is required (use {} when empty).");
  } else {
    try {
      JSON.parse(config);
    } catch (error) {
      setError(errors, "inputProfile.config", "Config must be valid JSON.");
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateProviderForm(model) {
  const errors = {};

  validateCommonFields(model, errors);

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
